import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ConfigService } from '../../services/config.service';

@Injectable({
	providedIn: 'root'
})
export class WorkflowQueryService {

	selectedQueries: number[] = [];
	selectedQueryLists: number[] = [];
	selectedNER: string[] = [];

	constructor(
		private configService: ConfigService,
		private http: HttpClient
	) { }

	async getQueries(page: number = 1, paging: any = {}, constraints: any = {}): Promise<any> {
		let url = this.configService.getConfig().dx.queriesURL;
		url += `?page=${page}`;
		url += `&size=${paging.elementsPerPage}`;
		url += `&sort=${paging.sorting.sort}`;
		url += `&constraints=${encodeURIComponent(JSON.stringify(constraints))}`;

		try {
			const response = await firstValueFrom(this.http.get<any>(url, {}));

			if (response?.content) {
				let queries = response.content;
				queries.forEach((query: any) => {
					this.formatQuery(query);
				});
				return response;
			} else {
				return Promise.reject(false);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getBatchQueries(batchid: number): Promise<any> {
		let url = this.configService.getConfig().dx.queriesURL;
		url += `/batch/${batchid}`;

		try {
			const response = await firstValueFrom(this.http.get<any>(url, {}));
			let queries = response;
			queries.forEach((query: any) => {
				this.formatQuery(query);
			});
			return queries;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	private formatQuery(query: any) {
		if (!query) return;

		if (query.queryListNames) {
			query.queryListNamesStr = query.queryListNames.join();
		}
	}

	setSelectedQueries(items: number[]) {
		this.selectedQueries.push(...items);
	}

	selectQuery(item: number) {
		if (this.selectedQueries.indexOf(item) == -1) {
			this.selectedQueries.push(item);
		}
	}

	deselectQuery(item: number) {
		var index = this.selectedQueries.indexOf(item);
		if (index !== -1) this.selectedQueries.splice(index, 1);
	}

	getSelectedQueries() {
		let copy: number[] = [];
		copy.push(...this.selectedQueries);
		return copy;
	}

	clearSelectedQueries() {
		this.selectedQueries = [];
	}

	async getQueryLists(page: number = 1, paging: any = {}, constraints: any = {}): Promise<any> {
		let url = this.configService.getConfig().dx.querylistsURL;
		url += `?page=${page}`;
		url += `&size=${paging.elementsPerPage}`;
		url += `&sort=${paging.sorting.sort}`;
		url += `&constraints=${encodeURIComponent(JSON.stringify(constraints))}`;

		try {
			return await firstValueFrom(this.http.get<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}
	
	setSelectedQueryLists(items: number[]) {
		this.selectedQueryLists.push(...items);
	}

	selectQueryList(item: number) {
		if (this.selectedQueryLists.indexOf(item) == -1) {
			this.selectedQueryLists.push(item);
		}
	}

	deselectQueryList(item: number) {
		var index = this.selectedQueryLists.indexOf(item);
		if (index !== -1) this.selectedQueryLists.splice(index, 1);
	}

	getSelectedQueryLists() {
		let copy: number[] = [];
		copy.push(...this.selectedQueryLists);
		return copy;
	}

	clearSelectedQueryLists() {
		this.selectedQueryLists = [];
	}	
	
	async getNER(): Promise<any> {
		let url = this.configService.getConfig().dx.nerURL;
		url += `/config`;

		try {
			return await firstValueFrom(this.http.get<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}
	
	async isNEREnabled(): Promise<any> {
		let url = this.configService.getConfig().dx.nerURL;
		url += `/enabled`;

		try {
			const response = await firstValueFrom(this.http.get<any>(url, {}));
			return Promise.resolve(true);
		} catch (error) {
			return Promise.reject(error);
		}
	}	
	
	setSelectedNER(items: string[]) {
		this.selectedNER.push(...items);
	}

	selectNER(item: string) {
		if (this.selectedNER.indexOf(item) == -1) {
			this.selectedNER.push(item);
		}
	}

	deselectNER(item: string) {
		var index = this.selectedNER.indexOf(item);
		if (index !== -1) this.selectedNER.splice(index, 1);
	}

	getSelectedNER() {
		let copy: string[] = [];
		copy.push(...this.selectedNER);
		return copy;
	}

	clearSelectedNER() {
		this.selectedNER = [];
	}
	
	async updateQuery(id: number, payload: any): Promise<any> {
		let url = this.configService.getConfig().dx.queriesURL;
		url += `/${id}`;

		try {
			const response = await firstValueFrom(this.http.put<any>(url, payload));

			if (response?.content) {
				let query = response.content;
				this.formatQuery(query);
				return query;
			} else {
				return Promise.reject(false);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	}
	
	async createQuery(payload: any): Promise<any> {
		let url = this.configService.getConfig().dx.queriesURL;
		url += `/add`;

		try {
			const response = await firstValueFrom(this.http.post<any>(url, payload));

			if (response?.content) {
				let query = response.content;
				this.formatQuery(query);
				return query;
			} else {
				return Promise.reject(false);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	}	
	
	async getQueryByName(name: string): Promise<any> {
		let url = this.configService.getConfig().dx.queriesURL;
		url += `/name`;

		try {
			const response = await firstValueFrom(this.http.post<any>(url, name));

			if (response?.content) {
				let query = response.content;
				this.formatQuery(query);
				return query;
			} else {
				return Promise.reject(false);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	}	
	
	async getQueryById(id: number): Promise<any> {
		let url = this.configService.getConfig().dx.queriesURL;
		url += `/id`;

		try {
			const response = await firstValueFrom(this.http.post<any>(url, name));

			if (response?.content) {
				let query = response.content;
				this.formatQuery(query);
				return query;
			} else {
				return Promise.reject(false);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	}	
	
	async isOCRTunedSearchEnabled(): Promise<any> {
		let url = this.configService.getConfig().dx.baseWorkflowURL;
		url += `/enabled/ocrsearch`;

		try {
			const response = await firstValueFrom(this.http.get<any>(url, {}));
			return Promise.resolve(true);
		} catch (error) {
			return Promise.reject(error);
		}
	}	
}
